import React, { useCallback, useState } from "react";

import clsx from "clsx";
import get from "lodash/get";
import useToggle from "react-use/lib/useToggle";
import { useTranslation } from "react-i18next";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import { useQuery, useMutation } from "@apollo/client";
import { Route, Switch, useRouteMatch, Redirect, Link } from "react-router-dom";

import Screen from "./Screen";

import { QUERY_BOARDS } from "../../config/graphql/query";
import { DELETE_BOARD } from "../../config/graphql/mutation";

import useSocket from "../../lib/hooks/useSocket";

type ScreenMessage = {
  id: string;
  client: string;
  title: string;
  status: boolean;
};

const TableRow = ({ item, status }: { item: INameboard; status: boolean }) => {
  const { id, title, template } = item;

  const [visible, setVisible] = useToggle(false);

  const { t } = useTranslation(["screens", "common"]);

  const onClickPreview = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.stopPropagation();
  };

  const [onDelete] = useMutation(DELETE_BOARD, {
    refetchQueries: [
      { query: QUERY_BOARDS, variables: { sort: { title: "ASC" } } },
    ],
    variables: { id },
  });

  const onBeforeDelete = () => {
    onDelete();

    return setVisible(false);
  };

  return (
    <>
      <tr>
        <th scope="row">{title}</th>
        <td>{template}</td>
        <th
          className={clsx({
            "text-success": status,
            "text-danger": !status,
          })}
        >
          {status ? "Online" : "Offline"}
        </th>
        <td>
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
            <a
              href={item.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClickPreview}
              className="mr-2"
            >
              <Button size="sm" variant="primary">
                {t("screens:screensRouter.button.preview")}
              </Button>
            </a>
            <Link to={`/screen/${id}`}>
              <Button
                size="sm"
                variant="primary"
                onClick={() => setVisible(false)}
              >
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("screens:screensRouter.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("screens:screensRouter.modal.body", { title: item.title })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ScreensRoute = React.memo(() => {
  const match = useRouteMatch();

  const { t } = useTranslation(["screens", "common"]);

  const { data } = useQuery(QUERY_BOARDS, {
    variables: { sort: { title: "ASC" } },
  });

  const [status, setStatus] = useState<{
    [key: string]: ScreenMessage;
  }>({});

  const onMessage = useCallback((data) => {
    console.log("onMessage: ", data);

    setStatus(data);
  }, []);

  useSocket(`/network/status`, {
    message: onMessage,
  });

  const screens: Array<INameboard> = data?.nameboards ?? [];

  const renderScreen = useCallback(
    (item: INameboard) => (
      <TableRow
        key={`screen-${item.id}`}
        item={item}
        status={get(status, [item.id, "status"], false)}
      />
    ),
    [status]
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("screens:screensRouter.nav.screens")}
          </li>
        </ol>
      </nav>
      <div className="d-flex justify-content-end align-items-center mb-4">
        <div>
          <Link
            to={`${match.path}/new`}
            type="button"
            className="btn btn-primary"
          >
            {t("screens:screensRouter.addScreen")}
          </Link>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th scope="col">{t("screens:screensRouter.th.title")}</th>
              <th scope="col">{t("screens:screensRouter.th.template")}</th>
              <th scope="col">{t("screens:screensRouter.th.status")}</th>
              <th scope="col">{t("screens:screensRouter.th.actions")}</th>
            </tr>
          </thead>
          <tbody>{screens.map(renderScreen)}</tbody>
        </table>
      </div>
    </div>
  );
});

const ScreensRouter = React.memo(() => {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={path}>
        <ScreensRoute />
      </Route>
      <Route exact path={`${path}/new`}>
        <Screen />
      </Route>
      <Route path={`${path}/:screenId`}>
        <Screen />
      </Route>
      <Redirect to={path} />
    </Switch>
  );
});

export default ScreensRouter;
